import {useParams} from 'react-router';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useIntl, FormattedMessage} from 'react-intl';
import cn from 'classnames';
import ErrorIcon from '@material-ui/icons/Error';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import {Grid, Slider, Divider, CircularProgress} from '@material-ui/core';
import {BaseDialog, BaseDialogProps} from '../../../base-components/BaseDialog';
import {StudioTextField} from '../../../base-components/StudioTextField';
import {StudioTooltip} from '../../../base-components/StudioTooltip';
import {VideoDataProps, VideoMarkProps} from '../../../types/dataset/DatasetBinary';
import {RootState, useAppDispatch} from '../../../store';
import {finalize} from '../../../store/dataService/job';
import {useSelector} from 'react-redux';
import {forEach, isEmpty} from 'lodash';
import {JobData} from '../../../types/dataService/Job';
import './VideoConfigDialog.scss';
import {deleteDatasetMetadataByBatchId, getToken} from '../../../store/dataService';
import URL_CONST from '../../../config/url';
import {BinaryStrategies} from './../model/BinaryStrategiesDto';
import {ManualStrategy} from './../model/ManualStrategyDto';
import {SceneDetectionStrategy} from './../model/SceneDetectionStrategyDto';
import {AIDrivenStrategy} from './../model/AIDrivenStrategyDto';
import {VideoSlicer} from './VideoSlicer';
import SegmentCreator, {ExternalSegment} from './SegmentCreator/SegmentCreator';
import {BinaryStrategyTypes} from '../model/BinaryStrategyDto';

export type VideoConfigDialogProps = BaseDialogProps & {
  binaryId: string;
  datasetId: string;
  onKFDSubmit: () => void;
  data?: VideoDataProps[];
};

const DEFAULT_CAPTURE_FRAMES_EVERY_IN_SECONDS = 2;

export function VideoConfigDialog(props: VideoConfigDialogProps) {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const {projectId} = useParams<{projectId: string}>();

  const [segments, setSegments] = useState<Array<ExternalSegment>>([]);
  const [captureFramesEveryInSeconds, setCaptureFramesEveryInSeconds] = useState(
    DEFAULT_CAPTURE_FRAMES_EVERY_IN_SECONDS
  );

  const {
    binaryId,
    datasetId,
    onKFDSubmit = () => {},

    onSubmit = () => {},
  } = props;
  const jobs = useSelector((state: RootState) => state.datasetJob.jobs);

  const [videoMarks, setVideoMarks] = useState<VideoMarkProps[]>([]);

  const selectedSecondsInTimeline = useMemo(() => videoMarks.map(mark => mark.value), [
    videoMarks,
  ]);
  const [thresholdStrategyValue, setThresholdStrategyValue] = useState(0.4);
  const [activeBinaryStrategyName, setActiveBinaryStrategyName] = useState(
    ManualStrategy.STRATEGY_NAME
  );

  const binaryStrategies = useMemo(
    () =>
      new BinaryStrategies({
        binaryId,
        datasetId,
        projectId,
        activeStrategyName: activeBinaryStrategyName,
        strategiesContext: {
          cleverIADriven: {},
          sceneDetection: {
            detectorThreshold: thresholdStrategyValue,
          },
          manual: {
            segments,
            captureFramesEveryInSeconds,
            frameExtractionTimes: selectedSecondsInTimeline,
          },
        },
      }),
    [
      segments,
      binaryId,
      datasetId,
      projectId,
      thresholdStrategyValue,
      activeBinaryStrategyName,
      selectedSecondsInTimeline,
      captureFramesEveryInSeconds,
    ]
  );
  const strategyOptions = useMemo(
    () => [
      ManualStrategy.STRATEGY_NAME,
      SceneDetectionStrategy.STRATEGY_NAME,
      AIDrivenStrategy.STRATEGY_NAME,
    ],
    []
  );

  const playerRef = useRef<HTMLVideoElement>(null);
  const [videoCurrentTime, setVideoCurrentTime] = useState(
    playerRef?.current?.currentTime ?? 0
  );
  const [canIPlayVideo, setCanIPlayVideo] = useState(false);
  const videoDuration = useMemo(
    () => (canIPlayVideo ? playerRef?.current?.duration ?? 0 : 0),
    [canIPlayVideo]
  );

  useEffect(() => {
    if (playerRef?.current) {
      playerRef.current.currentTime = videoCurrentTime;
    }
  }, [videoCurrentTime]);

  useEffect(() => {
    if (binaryId) {
      setVideoCurrentTime(0);
    }
  }, [binaryId]);

  const [disableSubmit, setDisableSubmit] = useState(false);
  const [binaryJob, setBinaryJob] = useState<JobData[] | undefined>();
  const [openConfirmPreGen, setOpenConfirmPreGen] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);

  useEffect(() => {
    const jobData: JobData[] = [];

    if (jobs) {
      forEach(jobs, (job: JobData) => {
        if (job.request && job.request.binaryId && job.request.binaryId === binaryId) {
          jobData.push(job);
        }
      });

      setBinaryJob(jobData);
    }
  }, [jobs, binaryId]);

  const onClose = () => {
    setSegments([]);
    setVideoMarks([]);
    props.onClose && props.onClose();

    setDisableSubmit(false);

    setIsPlaying(false);
  };

  const onPlayToggle = () => {
    const video = playerRef?.current;
    if (!isPlaying) {
      video?.play();
    } else {
      video?.pause();
    }
    setIsPlaying(!isPlaying);
  };

  type localSubmitSettings = {
    avoidValidation?: boolean;
  };

  const onLocalSubmit = ({avoidValidation = false}: localSubmitSettings = {}) => {
    if (!avoidValidation && !isEmpty(binaryJob)) {
      setOpenConfirmPreGen(true);
    } else {
      setDisableSubmit(true);

      if (binaryStrategies.getStrategyByName(AIDrivenStrategy.STRATEGY_NAME).isActive) {
        onKFDSubmit();
      }

      binaryStrategies.generateImages().then(() => {
        setDisableSubmit(false);
        onClose();
        onSubmit();
      });
    }
  };

  const onAppend = () => {
    setOpenConfirmPreGen(false);
    onLocalSubmit({avoidValidation: true});
  };

  const onOverwrite = async () => {
    setOpenConfirmPreGen(false);

    if (datasetId) {
      // 1 delete previous images with job.correlationId === binary.batchId
      const batchIds = binaryJob?.map(job => job.request?.correlationId).join(',');

      if (batchIds) {
        await dispatch(deleteDatasetMetadataByBatchId({datasetId, batchIds}));
      }
      // 2 finalize previous job/s(delete);
      forEach(binaryJob, job => {
        dispatch(finalize({datasetId, jobId: job.id}));
      });
    }

    onLocalSubmit({avoidValidation: true});
  };

  const strategyIntlKeyPostFix = useMemo(() => {
    return binaryStrategies.getStrategyByName(activeBinaryStrategyName).getIntlKeyName();
  }, [binaryStrategies, activeBinaryStrategyName]);

  return (
    <>
      <BaseDialog
        title={intl.formatMessage({
          id: 'dataPrep.video.config.title',
        })}
        {...props}
        className={cn('video-config-dialog', props.className)}
        onClose={onClose}
        onSubmit={onLocalSubmit}
        disableSubmit={disableSubmit || !canIPlayVideo}
        submitLabel={intl.formatMessage({
          id: `dataPrep.strategies.submit.${strategyIntlKeyPostFix}`,
        })}
      >
        <section className="video-config-dialog__header">
          <div className="video-config-dialog__header__strategy-container">
            <FormattedMessage tagName="p" id="dataPrep.binaryStrategies.title" />

            <RadioGroup
              name="choice"
              aria-label="choice"
              value={activeBinaryStrategyName}
              className="video-config-dialog__header__strategy-container__radios"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const strategyName = (event.target as HTMLInputElement)
                  .value as BinaryStrategyTypes;

                setActiveBinaryStrategyName(strategyName);
              }}
            >
              {strategyOptions.map((strategyName, index) => {
                const strategy = binaryStrategies.getStrategyByName(strategyName);

                return (
                  <FormControlLabel
                    value={strategyName}
                    key={`binary-strategy-option-${index}`}
                    control={<Radio color="primary" />}
                    label={intl.formatMessage({
                      id: `dataPrep.strategies.${strategy.getIntlKeyName()}`,
                    })}
                  />
                );
              })}
            </RadioGroup>
          </div>

          <FormattedMessage
            tagName="p"
            id={`dataPrep.strategies.description.${strategyIntlKeyPostFix}`}
          />
        </section>

        <div className="video-config-dialog__container">
          {binaryId && datasetId && (
            <video
              src={`${
                URL_CONST.DSS_DATASET_BINARIES
              }?binaryId=${binaryId}&datasetId=${datasetId}&authToken=${getToken()}`}
              ref={playerRef}
              width="100%"
              height="100%"
              preload="metadata"
              onCanPlay={() => {
                setCanIPlayVideo(true);
              }}
              onLoadStart={() => {
                setCanIPlayVideo(false);
              }}
            >
              Sorry, your browser doesn't support embedded videos.
            </video>
          )}
          {!canIPlayVideo && (
            <div className="video-config-dialog__loader">
              <CircularProgress />
            </div>
          )}
        </div>

        <div className="video-config-dialog__range_container">
          <VideoSlicer
            disabled={!canIPlayVideo}
            videoDuration={videoDuration}
            currentTime={videoCurrentTime}
            disableMarks={
              binaryStrategies.getStrategyByName(ManualStrategy.STRATEGY_NAME).isDisabled
            }
            onChange={({currentTime, marks, isPlaying: shouldPlay}) => {
              if (currentTime !== undefined && playerRef.current) {
                setVideoCurrentTime(currentTime);
              }

              if (marks && JSON.stringify(marks) !== JSON.stringify(videoMarks)) {
                setVideoMarks(marks);
              }

              if (shouldPlay !== undefined && shouldPlay !== isPlaying) {
                onPlayToggle();
              }

              // reset video time if it's the end of the video and still playing
              if (currentTime === videoDuration && isPlaying) {
                setVideoCurrentTime(0);
              }
            }}
          />
        </div>
        <Divider light className="video-config-dialog__field_divider" />

        {binaryStrategies.MANUAL_STRATEGY.isActive && (
          <Grid container alignContent="flex-start">
            <Grid container className="video-config-dialog__field_label_container">
              <b>
                <p className="video-config-dialog__field_label">
                  {intl.formatMessage({
                    id: 'dataPrep.binaryStrategies.manual.createSegments.label',
                  })}
                </p>
              </b>
            </Grid>

            <Grid container>
              <SegmentCreator
                segments={segments}
                timelineMinSeconds={0}
                timelineMaxSeconds={videoDuration}
                onSegmentListEmptied={() => {
                  if (segments.length) {
                    setSegments([]);
                  }
                }}
                onBlur={segmentsList => {
                  if (JSON.stringify(segmentsList) !== JSON.stringify(segments)) {
                    setSegments(segmentsList);
                  }
                }}
              />
            </Grid>
          </Grid>
        )}

        {binaryStrategies.MANUAL_STRATEGY.isActive && (
          <Grid container spacing={1} alignItems="center" alignContent="flex-start">
            <Grid item md={12} className="video-config-dialog__field_label_container">
              <span className="video-config-dialog__field_label">
                <b>
                  {intl.formatMessage({
                    id: `dataPrep.video.config.frame.label.${
                      segments.length ? 'withinSegments' : 'withoutSegments'
                    }`,
                  })}
                </b>
              </span>
            </Grid>

            <Grid item className="video-config-dialog__field_label_container">
              <span className="video-config-dialog__field_label">
                {intl.formatMessage({
                  id: 'dataPrep.video.config.frame.label.prefix',
                })}
              </span>
            </Grid>
            <Grid item md={2}>
              <StudioTextField
                type="number"
                inputProps={{
                  min: 1,
                }}
                InputProps={{
                  value: captureFramesEveryInSeconds,
                  onChange: event => {
                    setCaptureFramesEveryInSeconds(Number(event.target.value));
                  },
                }}
              />
            </Grid>
            <Grid item className="video-config-dialog__field_label_container">
              <span className="video-config-dialog__field_label">
                {intl.formatMessage({
                  id: 'dataPrep.video.config.frame.label.postfix',
                })}
              </span>
            </Grid>
          </Grid>
        )}

        {binaryStrategies.SCENE_DETECTION_STRATEGY.isActive && (
          <Grid container spacing={1} alignContent="center">
            <Grid item md={5} className="video-config-dialog__field_label_container">
              <b>
                <p className="video-config-dialog__field_label">
                  {intl.formatMessage({
                    id: 'dataPrep.binaryStrategies.threshold.threshold.label',
                  })}
                </p>
              </b>
              <p className="video-config-dialog__field_label">
                {intl.formatMessage({
                  id: 'dataPrep.binaryStrategies.threshold.threshold.description',
                })}
              </p>
            </Grid>

            <Grid
              item
              md={1}
              alignContent="flex-end"
              className="video-config-dialog__field_label_container"
            >
              <p className="video-config-dialog__field_value">{thresholdStrategyValue}</p>
            </Grid>

            <Grid item md={6} alignContent="flex-end">
              <Slider
                valueLabelDisplay="auto"
                aria-labelledby="range-slider"
                ValueLabelComponent={ValueLabelComponent}
                className={cn('video-config-dialog__threshold_range')}
                max={SceneDetectionStrategy.SCENE_DETECTOR_THRESHOLD_MAX}
                min={SceneDetectionStrategy.SCENE_DETECTOR_THRESHOLD_MIN}
                step={SceneDetectionStrategy.SCENE_DETECTOR_THRESHOLD_STEP}
                disabled={binaryStrategies.SCENE_DETECTION_STRATEGY.isDisabled}
                value={binaryStrategies.SCENE_DETECTION_STRATEGY.sceneDetectorThreshold}
                onChange={(event, val) => {
                  event.preventDefault();
                  setThresholdStrategyValue(val as number);
                }}
              />
            </Grid>
          </Grid>
        )}
      </BaseDialog>
      {/* Append new images or amend previous images */}
      <ConfirmDialog
        open={openConfirmPreGen}
        onAppend={onAppend}
        onOverwrite={onOverwrite}
        onClose={() => setOpenConfirmPreGen(false)}
      />
    </>
  );
}

interface ValueLabelComponentProps {
  children: React.ReactElement;
  open: boolean;
  value: number;
}

function ValueLabelComponent(props: ValueLabelComponentProps) {
  const {children, open, value} = props;

  return (
    <StudioTooltip open={open} enterTouchDelay={0} placement="top" title={value}>
      {children}
    </StudioTooltip>
  );
}

export const ConfirmDialog = ({
  onClose,
  onAppend,
  onOverwrite,
  open,
}: {
  open: boolean;
  onClose: () => void;
  onAppend: () => void;
  onOverwrite: () => void;
}) => {
  const intl = useIntl();
  const [choice, setChoice] = useState('append');

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      setChoice((event.target as HTMLInputElement).value),
    []
  );

  const onSubmit = useCallback(() => {
    const choiceFunc = choice === 'append' ? onAppend : onOverwrite;

    choiceFunc();
  }, [choice, onAppend, onOverwrite]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      id="confirm-dialog"
      className="confirm-dialog"
      data-testid="confirm-dialog"
      aria-labelledby="confirm-dialog"
      aria-describedby="confirm-dialog-description"
    >
      <DialogTitle>
        <div className="confirm-dialog__title-container">
          <ErrorIcon fontSize="medium" color="secondary" />

          <span className="confirm-dialog__title">
            <FormattedMessage id="dataPrep.video.regen.title" />
          </span>
        </div>
      </DialogTitle>
      <DialogContent>
        <DialogContentText className="confirm-dialog__message">
          <FormattedMessage id="dataPrep.video.regen.desc" />
          <RadioGroup
            className="confirm-dialog__message_choice"
            aria-label="choice"
            name="choice"
            value={choice}
            onChange={handleChange}
          >
            <FormControlLabel
              value="append"
              control={<Radio color="primary" />}
              label={intl.formatMessage({
                id: 'dataPrep.video.regen.choice.append',
              })}
            />
            <FormControlLabel
              value="overwrite"
              control={<Radio color="primary" />}
              label={intl.formatMessage({
                id: 'dataPrep.video.regen.choice.overwrite',
              })}
            />
          </RadioGroup>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          <FormattedMessage id="form.cancel" />
        </Button>

        <Button
          color="primary"
          disableElevation
          variant="contained"
          id="confirm-dialog-ok"
          data-testid="confirm-dialog-ok"
          onClick={onSubmit}
        >
          <FormattedMessage id="form.confirm" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
